import {
  Form,
  FormField,
  FormLabel,
  FormDescription,
  FormControl,
  FormMessage,
  FormItem,
} from "@/components/ui/form";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";

import { UserData } from "modules/authentication/types/user-data";
import {
  updateAccountEndPoint,
  settingsLocationEndPoint,
} from "modules/main/services/endpoints";
import { toast } from "sonner";
import { useMediaQuery } from "@/hooks/use-media-query";
import { isDesktopSize } from "utils/is-desktop";
import { SectionHeader, SectionMiniHeader } from "./section-header";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFetch } from "modules/main/hooks/useFetch";
import { FetchDataOptions } from "modules/main/services/fetchData";
import { LocationResponse } from "../types/location-types";

const profileFormShema = z
  .object({
    firstName: z
      .string()
      .min(2, { message: "El nombre debe tener más de 2 carácteres" }),
    lastName: z
      .string()
      .min(2, { message: "El apellido debe tener más de 2 carácteres" }),
    city_id: z.string().min(1, { message: "Debes seleccionar una comuna" }),
    location: z.string().min(1, { message: "Debes seleccionar un sector" }),
    username: z.string().email({ message: "El email no es válido" }),
    password: z
      .string()
      .min(8, { message: "La contraseña debe tener más de 8 carácteres" })
      .regex(/[a-zA-Z]/, { message: "Debe contener al menos una letra" })
      .regex(/[0-9]/, { message: "Debe contener al menos un número" }),
    passwordConfirmation: z
      .string()
      .min(8, { message: "La contraseña debe tener más de 8 carácteres" }),
  })
  .refine(
    (data) => {
      return data.password === data.passwordConfirmation;
    },
    { message: "Las contraseñas no coinciden", path: ["passwordConfirmation"] },
  );

type profileFormvalues = z.infer<typeof profileFormShema>;
const userData = JSON.parse(
  localStorage.getItem("userData") ?? "{}",
) as UserData;

const defaultValues: Partial<profileFormvalues> = {
  firstName: userData.first_name,
  lastName: userData.last_name,
  username: userData.username,
  password: "",
  city_id: userData.city_id?.toString() || "",
  location: userData.location_id?.toString() || "",
  passwordConfirmation: "",
};

export const ProfileForm = () => {
  const isDesktop = useMediaQuery(isDesktopSize);
  const [selectedCityId, setSelectedCityId] = useState<string>(
    defaultValues.city_id || "",
  );
  const [filteredLocations, setFilteredLocations] = useState<
    Array<{ id: number; name: string; city_id: number }>
  >([]);

  // Usando useFetch para obtener datos de ubicación
  const requestOptions: FetchDataOptions = {
    method: "GET",
    headers: {},
    credentials: "include",
  };

  const {
    data: locationData,
    loading,
    error,
  } = useFetch<LocationResponse>(settingsLocationEndPoint, requestOptions);

  const form = useForm<profileFormvalues>({
    resolver: zodResolver(profileFormShema),
    defaultValues,
    mode: "onChange",
  });

  // Procesar datos cuando se cargan desde el endpoint
  useEffect(() => {
    if (locationData) {
      // Si hay un cityId seleccionado, filtrar las locations correspondientes
      if (selectedCityId) {
        const cityLocations = locationData.locations.filter(
          (loc) => loc.city_id === parseInt(selectedCityId),
        );
        setFilteredLocations(cityLocations);
      }
    }
  }, [locationData, selectedCityId]);

  // Watch for city changes to filter locations
  useEffect(() => {
    const cityId = form.watch("city_id");

    if (cityId && locationData?.locations) {
      setSelectedCityId(cityId);
      const cityLocations = locationData.locations.filter(
        (loc) => loc.city_id === parseInt(cityId),
      );
      setFilteredLocations(cityLocations);

      // Reset location selection if the city changes
      if (cityId !== selectedCityId) {
        form.setValue("location", "");
      }
    }
  }, [form, selectedCityId, locationData, form.watch("city_id")]);

  // Mostrar mensaje de error si hay un problema al cargar los datos
  useEffect(() => {
    if (error) {
    }
  }, [error]);

  const updateLocalStorage = (data: profileFormvalues) => {
    userData.first_name = data.firstName;
    userData.last_name = data.lastName;
    userData.username = data.username;
    userData.city_id = parseInt(data.city_id);
    userData.location_id = parseInt(data.location);

    // Find the selected city and location to store their names
    const selectedCity = locationData?.cities.find(
      (city) => city.id === parseInt(data.city_id),
    );
    const selectedLocation = locationData?.locations.find(
      (loc) => loc.id === parseInt(data.location),
    );

    if (selectedCity) {
      userData.city_name = selectedCity.name;
    }

    if (selectedLocation) {
      userData.location_name = selectedLocation.name;
    }

    localStorage.setItem("userData", JSON.stringify(userData));
  };

  const onSubmit = async (data: profileFormvalues) => {
    try {
      const formData = {
        ...data,
        // city_id: parseInt(data.city_id),
        location: parseInt(data.location),
      };

      const response = await fetch(updateAccountEndPoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Error al actualizar los datos");
      }

      const responseData = (await response.json()) as {
        status: boolean;
        message?: string;
      };
      if (responseData.status) {
        toast("Tus datos han sido actualizados correctamente");
        updateLocalStorage(data);
      } else {
        toast(
          responseData.message ||
            "Ha ocurrido un error al actualizar los datos",
        );
      }
    } catch (e) {
      toast("Ha ocurrido un error al actualizar los datos");
    }
  };

  return (
    <>
      {!isDesktop && (
        <SectionHeader
          title="Ajustes de cuenta"
          description=" Manten actualizado los datos de tú cuenta"
          className="mb-4"
        />
      )}
      <Form {...form}>
        {isDesktop && (
          <SectionMiniHeader
            className="mb-4"
            title="Ajustes de cuenta"
            description="Manten actualizado los datos de tú cuenta"
          />
        )}

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col space-y-4 mt-4"
        >
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre</FormLabel>
                <FormControl>
                  <Input placeholder="Nombre" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Apellidos</FormLabel>
                <FormControl>
                  <Input placeholder="Apellidos" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="Email" type="email" {...field} />
                </FormControl>
                <FormDescription>
                  Esto se usará como tu nombre de usuario.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* City (Comuna) field */}
          <FormField
            control={form.control}
            name="city_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Comuna</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={loading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona tu comuna" />
                    </SelectTrigger>
                    <SelectContent>
                      {loading ? (
                        <div className="flex justify-center p-2">
                          <div className="animate-spin h-5 w-5 border-2 border-primary rounded-full border-t-transparent"></div>
                        </div>
                      ) : (
                        <SelectGroup>
                          {locationData?.cities?.map((city) => (
                            <SelectItem
                              key={city.id}
                              value={city.id.toString()}
                            >
                              {city.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Location (sector) field */}
          <FormField
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Sector</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={
                      loading ||
                      !selectedCityId ||
                      filteredLocations.length === 0
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona tu sector" />
                    </SelectTrigger>
                    <SelectContent>
                      {loading ? (
                        <div className="flex justify-center p-2">
                          <div className="animate-spin h-5 w-5 border-2 border-primary rounded-full border-t-transparent"></div>
                        </div>
                      ) : (
                        <SelectGroup>
                          {filteredLocations.map((location) => (
                            <SelectItem
                              key={location.id}
                              value={location.id.toString()}
                            >
                              {location.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormDescription>
                  Selecciona primero una comuna para ver los sectores
                  disponibles
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contraseña</FormLabel>
                <FormControl>
                  <Input type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="passwordConfirmation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirmar Contraseña</FormLabel>
                <FormControl>
                  <Input type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit">Actualizar</Button>
        </form>
      </Form>
    </>
  );
};
